import { Fragment, useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { create } from 'zustand';

// Toast types
type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

// Toast store for global access
interface ToastStore {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  addToast: (toast) => {
    const id = Math.random().toString(36).substr(2, 9);
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }));
  },
  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },
}));

// Convenience functions
export const toast = {
  success: (title: string, message?: string, duration?: number) =>
    useToastStore.getState().addToast({ type: 'success', title, message, duration }),
  error: (title: string, message?: string, duration?: number) =>
    useToastStore.getState().addToast({ type: 'error', title, message, duration }),
  warning: (title: string, message?: string, duration?: number) =>
    useToastStore.getState().addToast({ type: 'warning', title, message, duration }),
  info: (title: string, message?: string, duration?: number) =>
    useToastStore.getState().addToast({ type: 'info', title, message, duration }),
};

// Icon mapping
const icons = {
  success: CheckCircleIcon,
  error: ExclamationCircleIcon,
  warning: ExclamationTriangleIcon,
  info: InformationCircleIcon,
};

const iconColors = {
  success: 'text-success-500',
  error: 'text-danger-500',
  warning: 'text-warning-500',
  info: 'text-accent-500',
};

const bgColors = {
  success: 'bg-success-50 dark:bg-success-500/10',
  error: 'bg-danger-50 dark:bg-danger-500/10',
  warning: 'bg-warning-50 dark:bg-warning-500/10',
  info: 'bg-accent-50 dark:bg-accent-500/10',
};

// Single toast component
function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const [isShowing, setIsShowing] = useState(true);
  const Icon = icons[toast.type];
  const duration = toast.duration ?? 5000;

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsShowing(false);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration]);

  return (
    <Transition
      show={isShowing}
      as={Fragment}
      enter="transform ease-out duration-300 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      afterLeave={onClose}
    >
      <div
        className={`
          pointer-events-auto w-full max-w-sm overflow-hidden rounded-xl
          bg-white dark:bg-surface-800
          shadow-lg border border-surface-200 dark:border-surface-700
          ring-1 ring-black/5
        `}
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div
              className={`
                flex-shrink-0 w-10 h-10 rounded-full
                flex items-center justify-center
                ${bgColors[toast.type]}
              `}
            >
              <Icon className={`w-5 h-5 ${iconColors[toast.type]}`} />
            </div>
            <div className="flex-1 pt-0.5">
              <p className="text-sm font-semibold text-surface-900 dark:text-surface-100">
                {toast.title}
              </p>
              {toast.message && (
                <p className="mt-1 text-sm text-surface-500 dark:text-surface-400">
                  {toast.message}
                </p>
              )}
            </div>
            <button
              onClick={() => setIsShowing(false)}
              className="
                flex-shrink-0 rounded-lg p-1.5 -m-1.5
                text-surface-400 hover:text-surface-600
                dark:text-surface-500 dark:hover:text-surface-300
                hover:bg-surface-100 dark:hover:bg-surface-700
                transition-colors duration-150
                focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500
              "
            >
              <span className="sr-only">Dismiss</span>
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </Transition>
  );
}

// Toast container - renders all toasts
export function ToastContainer() {
  const { toasts, removeToast } = useToastStore();

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-50 flex flex-col items-end px-4 py-6 sm:items-end sm:p-6"
    >
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onClose={() => removeToast(t.id)} />
        ))}
      </div>
    </div>
  );
}

export default ToastContainer;
